import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { Toggle } from '@fluentui/react';
import { cutToast } from 'utility/toast';
import { FilterArrayType } from 'common/components/Filters';
import {
  EntitySelectionInput,
  PurchaseOrderExportFilterInput,
  SearchRequestInput,
} from 'common/types/globalTypes';
import React, { useState } from 'react';
import { toFilterVariableExport } from '../utils';
import {
  GenerateIntegrationStatusSub,
  GenerateIntegrationStatusSubVariables,
} from './__generated__/GenerateIntegrationStatusSub';
import {
  GeneratePurchaseOrderIntegration,
  GeneratePurchaseOrderIntegrationVariables,
} from './__generated__/GeneratePurchaseOrderIntegration';
import { PurchaseOrderAccountingIntegrationAllowed } from './__generated__/PurchaseOrderAccountingIntegrationAllowed';
import {
  PurchaseOrderExportFilter,
  PurchaseOrderExportFilterVariables,
} from './__generated__/PurchaseOrderExportFilter';
import {
  PurchaseOrderExportSearch,
  PurchaseOrderExportSearch_purchaseOrderExportSearch,
  PurchaseOrderExportSearchVariables,
} from './__generated__/PurchaseOrderExportSearch';
import toast from 'react-hot-toast';
import { PurchaseOrderItem } from '../poListComponents';
import {
  AccountingExportCycleFn,
  AccountingExportToCsv,
} from 'common/components/AccountingExportToCsv';
import { toSelection } from 'common/utils/entity';

import ACCOUNTING_TRANSFER_ALLOWED from './PurchaseOrderAccountingIntegrationAllowed.graphql';
import PURCHASE_ORDER_EXPORT_FILTER from './PurchaseOrderExportFilter.graphql';
import INTEGRATION_STATUS from './GenerateIntegrationStatusSub.graphql';
import PURCHASE_ORDER_INTEGRATION from './GeneratePurchaseOrderIntegration.graphql';
import PURCHASE_ORDER_EXPORT_SEARCH from './PurchaseOrderExportSearch.graphql';

interface ExportPurchaseOrdersProps {
  selectedRows: PurchaseOrderItem[];
  isSelectedAll: boolean;
  isSigningSearch: boolean;
  searchFilters: FilterArrayType[];
  purchaseOrderDocumentSearchRequest: SearchRequestInput | null;
  isHistorySearch: boolean;
  isSearchAllData: boolean;
  isLinkSearch: boolean;
  filterSupplierId: string | undefined;
}

export const ExportPurchaseOrders: React.FC<ExportPurchaseOrdersProps> = ({
  selectedRows,
  isSelectedAll,
  isSigningSearch,
  searchFilters,
  purchaseOrderDocumentSearchRequest,
  isHistorySearch,
  isSearchAllData,
  isLinkSearch,
  filterSupplierId,
}) => {
  const client = useApolloClient();
  const [isAccountingTransfer, setAccountingTransfer] = useState(false);

  const [purchaseOrderExportSearch] = useLazyQuery<
    PurchaseOrderExportSearch,
    PurchaseOrderExportSearchVariables
  >(PURCHASE_ORDER_EXPORT_SEARCH);

  const [generatePurchaseOrderIntegration] = useMutation<
    GeneratePurchaseOrderIntegration,
    GeneratePurchaseOrderIntegrationVariables
  >(PURCHASE_ORDER_INTEGRATION, { errorPolicy: 'all' });

  const [purchaseOrderExportFilter] = useMutation<
    PurchaseOrderExportFilter,
    PurchaseOrderExportFilterVariables
  >(PURCHASE_ORDER_EXPORT_FILTER);

  const { data: accountingTransferAllowed } =
    useQuery<PurchaseOrderAccountingIntegrationAllowed>(
      ACCOUNTING_TRANSFER_ALLOWED
    );

  const getExportCycle: AccountingExportCycleFn = async (
    desc,
    acctPeriod,
    includeDist
  ) => {
    let exportVariable: PurchaseOrderExportFilterInput = {
      exportDescription: desc,
      corporatePeriodId: acctPeriod,
      isDistributionIncluded: includeDist,
    };

    if (isSelectedAll) {
      const filterVariable = toFilterVariableExport(searchFilters ?? []);
      exportVariable = {
        ...exportVariable,
        searchRequest: purchaseOrderDocumentSearchRequest,
        isHistorySearch,
        isLinkSearch,
        isSearchAllData,
        isSigningSearch,
        isAccountingTransfer,
        filterSupplierId,
        ...filterVariable,
      };
    } else if (selectedRows.length > 0) {
      exportVariable = {
        ...exportVariable,
        entityDelete: selectedRows.map(toSelection),
      };
    }

    const { errors, data } = await purchaseOrderExportFilter({
      variables: { input: exportVariable },
    });

    return {
      id: data?.purchaseOrderExportFilter?.exportResult?.exportCycles?.[0],
      errors,
    };
  };

  const getAllPurchaseOrders = async () => {
    const filterVariable = toFilterVariableExport(searchFilters ?? []);
    const { data, errors } = await purchaseOrderExportSearch({
      variables: {
        searchRequest: purchaseOrderDocumentSearchRequest,
        isHistorySearch,
        isLinkSearch,
        isSearchAllData,
        isSigningSearch,
        isAccountingTransfer,
        ...filterVariable,
      },
    });

    if (errors) {
      cutToast.error(errors?.[0].message);
    }

    return data?.purchaseOrderExportSearch || [];
  };

  const handleIntegration = async () => {
    const entitySelection: EntitySelectionInput[] = isSelectedAll
      ? (
          await getAllPurchaseOrders()
        ).filter<PurchaseOrderExportSearch_purchaseOrderExportSearch>(
          (po) => !!po
        )
      : selectedRows.map(toSelection);

    if (entitySelection.length > 0) {
      const { errors, data } = await generatePurchaseOrderIntegration({
        variables: {
          input: {
            entitySelection,
          },
        },
      });
      if (!!errors?.length) {
        cutToast.error(errors?.[0].message);
      } else {
        if (!!data?.generatePurchaseOrderIntegration?.integrationDocumentId) {
          cutToast.info(`Export process submitted.`);
          const integrationDocumentId =
            data?.generatePurchaseOrderIntegration?.integrationDocumentId;
          const observer = client.subscribe<
            GenerateIntegrationStatusSub,
            GenerateIntegrationStatusSubVariables
          >({
            query: INTEGRATION_STATUS,
            variables: {
              integrationDocumentId: integrationDocumentId,
            },
          });
          const subscription = observer.subscribe(({ data, errors }) => {
            if (errors) {
              cutToast.error(
                'Errors received while Subscribing to generate integration status'
              );
            } else {
              const { status } = {
                ...data?.generateIntegrationStatus,
              };
              if (status === 'SUCCESS') {
                cutToast.success(
                  'Export file successfully created in the export folder'
                );
              }
              if (status === 'FAILURE') {
                cutToast.error(
                  'Error occurred while creating export file in the export folder'
                );
              }
            }
            subscription.unsubscribe();
          });
        }
      }
    }
  };

  const isAccountingTransferDisabled =
    isSigningSearch ||
    isHistorySearch ||
    !accountingTransferAllowed?.purchaseOrderAccountingIntegrationAllowed;
  const additionalComponents = (
    <Toggle
      inlineLabel
      label="Accounting Transfer"
      disabled={isAccountingTransferDisabled}
      onChange={(_, value) => {
        setAccountingTransfer(!!value);
      }}
    />
  );

  return (
    <AccountingExportToCsv
      isButtonVisible={isSelectedAll || selectedRows.length > 0}
      title="Enter the description and press confirm to download the Purchase Order worksheet."
      additionalComponents={additionalComponents}
      onDialogOpen={() => {
        setAccountingTransfer(false);
      }}
      getExportCycle={getExportCycle}
      onConfirmOverride={({ confirm, close }) => {
        if (isAccountingTransfer) {
          handleIntegration();
          close();
        } else {
          confirm();
        }
      }}
    />
  );
};
